import React, { useState } from 'react';
import {
  Card,
  Button,
  Row,
  Col,
  Alert,
  Spinner,
} from 'react-bootstrap';
import {
  FaCertificate,
  FaDownload,
  FaCheckCircle,
  FaHourglassHalf,
  FaFilePdf,
  FaImage,
  FaExternalLinkAlt,
} from 'react-icons/fa';
import { toast } from 'react-toastify';
import CertificateService from '../../../services/certificateService';

const StudentCertificateTab = ({ batchInfo, courseName, studentName }) => {
  const [loading, setLoading] = useState(false);
  
  const { batch, certificate } = batchInfo || {};
  const hasCertificate = certificate !== null && certificate !== undefined;

  // NEW: Open certificate in new tab with actions
  const handleOpenInNewTab = async () => {
    if (!hasCertificate) return;
    
    try {
      setLoading(true);
      
      // First, get the certificate file as blob
      const response = await CertificateService.viewCertificate(certificate.id);
      
      // Create a blob URL
      const url = window.URL.createObjectURL(new Blob([response.data]));
      
      // Get filename
      const contentDisposition = response.headers['content-disposition'];
      let filename = certificate.certificate_name || 'certificate.pdf';
      if (contentDisposition) {
        const filenameMatch = contentDisposition.match(/filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/);
        if (filenameMatch && filenameMatch[1]) {
          filename = filenameMatch[1].replace(/['"]/g, '');
        }
      }

      // Determine file type
      const fileExtension = filename.split('.').pop().toLowerCase();
      const isPDF = fileExtension === 'pdf';
      const isImage = ['jpg', 'jpeg', 'png', 'gif'].includes(fileExtension);

      // Create new tab content with actions
      const newTab = window.open('', '_blank');
      if (!newTab) {
        toast.error('Please allow pop-ups to open certificate');
        return;
      }

      // Write HTML with certificate display and action buttons
      newTab.document.write(`
        <!DOCTYPE html>
        <html>
        <head>
          <title>Certificate - ${certificate.certificate_number}</title>
          <meta charset="utf-8">
          <meta name="viewport" content="width=device-width, initial-scale=1">
          <style>
            body {
              font-family: Arial, sans-serif;
              background: #f5f5f5;
              margin: 0;
              padding: 20px;
              display: flex;
              flex-direction: column;
              align-items: center;
            }
            .container {
              max-width: 900px;
              width: 100%;
            }
            .actions {
              display: flex;
              gap: 10px;
              justify-content: center;
              flex-wrap: wrap;
              margin-bottom: 20px;
              padding: 15px;
              background: white;
              border-radius: 8px;
              box-shadow: 0 2px 5px rgba(0,0,0,0.1);
              position: sticky;
              top: 10px;
              z-index: 100;
            }
            .btn {
              padding: 12px 24px;
              border: none;
              border-radius: 5px;
              cursor: pointer;
              font-size: 16px;
              font-weight: bold;
              display: inline-flex;
              align-items: center;
              gap: 8px;
              transition: all 0.3s;
              text-decoration: none;
            }
            .btn:hover {
              transform: translateY(-2px);
              box-shadow: 0 4px 10px rgba(0,0,0,0.2);
            }
            .btn-download {
              background: #28a745;
              color: white;
            }
            .btn-print {
              background: #007bff;
              color: white;
            }
            .btn-share {
              background: #17a2b8;
              color: white;
            }
            .btn-close {
              background: #6c757d;
              color: white;
            }
            .certificate-container {
              background: white;
              padding: 20px;
              border-radius: 8px;
              box-shadow: 0 2px 10px rgba(0,0,0,0.1);
              text-align: center;
            }
            .certificate-image {
              max-width: 100%;
              height: auto;
              border: 1px solid #ddd;
              border-radius: 4px;
              box-shadow: 0 2px 5px rgba(0,0,0,0.1);
            }
            .certificate-pdf {
              width: 100%;
              height: 80vh;
              border: none;
            }
            .info {
              margin-top: 20px;
              padding: 15px;
              background: #f8f9fa;
              border-radius: 5px;
              text-align: left;
            }
            .info p {
              margin: 5px 0;
              color: #666;
            }
            @media print {
              .actions { display: none; }
              body { background: white; padding: 0; }
              .certificate-container { box-shadow: none; padding: 0; }
            }
          </style>
        </head>
        <body>
          <div class="container">
            <div class="actions">
              <button class="btn btn-download" onclick="downloadCertificate()">
                ⬇️ Download Certificate
              </button>
              <button class="btn btn-print" onclick="window.print()">
                🖨️ Print
              </button>
              <button class="btn btn-share" onclick="shareCertificate()">
                📤 Share
              </button>
              <button class="btn btn-close" onclick="window.close()">
                ✕ Close
              </button>
            </div>

            <div class="certificate-container">
              <h2 class="text-center mb-4">Course Completion Certificate</h2>
              
              ${isPDF ? 
                `<iframe class="certificate-pdf" src="${url}#toolbar=0&navpanes=0" title="Certificate"></iframe>` : 
                isImage ?
                `<img class="certificate-image" src="${url}" alt="Certificate">` :
                `<p>Certificate file cannot be displayed directly. Please use download button.</p>`
              }

              <div class="info">
                <p><strong>Certificate No:</strong> ${certificate.certificate_number}</p>
                <p><strong>Student Name:</strong> ${studentName}</p>
                <p><strong>Course:</strong> ${courseName}</p>
                <p><strong>Batch:</strong> ${batch?.name}</p>
                <p><strong>Issue Date:</strong> ${new Date(certificate.issue_date).toLocaleDateString()}</p>
                <p><strong>File Name:</strong> ${filename}</p>
              </div>
            </div>
          </div>

          <script>
            function downloadCertificate() {
              const link = document.createElement('a');
              link.href = '${url}';
              link.download = '${filename}';
              document.body.appendChild(link);
              link.click();
              document.body.removeChild(link);
            }

            async function shareCertificate() {
              try {
                const response = await fetch('${url}');
                const blob = await response.blob();
                const file = new File([blob], '${filename}', { type: blob.type });
                
                if (navigator.share && navigator.canShare && navigator.canShare({ files: [file] })) {
                  await navigator.share({
                    title: 'Course Certificate',
                    text: 'Certificate from Institute LMS',
                    files: [file]
                  });
                } else {
                  // Fallback - copy link
                  navigator.clipboard.writeText(window.location.href);
                  alert('Certificate link copied to clipboard!');
                }
              } catch (err) {
                console.error('Share failed:', err);
                // Fallback
                navigator.clipboard.writeText(window.location.href);
                alert('Certificate link copied to clipboard!');
              }
            }
          </script>
        </body>
        </html>
      `);

      newTab.document.close();
      toast.success('Certificate opened in new tab');
      
    } catch (error) {
      console.error('Error opening certificate:', error);
      toast.error('Failed to open certificate');
    } finally {
      setLoading(false);
    }
  };

  const handleDownload = async () => {
    if (!hasCertificate) return;
    
    try {
      setLoading(true);
      await CertificateService.downloadCertificate(certificate.id);
      toast.success('Certificate download started');
    } catch (error) {
      toast.error('Failed to download certificate');
    } finally {
      setLoading(false);
    }
  };

  const getFileIcon = () => {
    if (!certificate?.certificate_name) return <FaCertificate />;
    
    const fileName = certificate.certificate_name.toLowerCase();
    if (fileName.endsWith('.pdf')) {
      return <FaFilePdf className="text-danger" size={40} />;
    } else if (fileName.match(/\.(jpg|jpeg|png|gif)$/)) {
      return <FaImage className="text-primary" size={40} />;
    }
    return <FaCertificate className="text-warning" size={40} />;
  };

  // If certificate is available
  if (hasCertificate) {
    return (
      <div className="text-center">
        {/* Certificate Preview Card */}
        <Card className="shadow-lg mb-4">
          <Card.Header className="bg-success text-white">
            <h4 className="mb-0">
              <FaCertificate className="me-2" />
              Your Certificate
            </h4>
          </Card.Header>
          <Card.Body className="p-4">
            <div className="mb-4">
              {getFileIcon()}
              <h5 className="mt-2">{certificate.certificate_name}</h5>
              <p className="text-muted">
                Certificate No: {certificate.certificate_number}
              </p>
            </div>

            {/* Certificate Info */}
            <Row className="mb-4">
              <Col md={6}>
                <Card className="bg-light">
                  <Card.Body>
                    <strong>Student Name:</strong>
                    <p className="mb-0">{studentName}</p>
                  </Card.Body>
                </Card>
              </Col>
              <Col md={6}>
                <Card className="bg-light">
                  <Card.Body>
                    <strong>Issue Date:</strong>
                    <p className="mb-0">
                      {new Date(certificate.issue_date).toLocaleDateString()}
                    </p>
                  </Card.Body>
                </Card>
              </Col>
            </Row>

            {/* Action Buttons */}
            <div className="d-flex justify-content-center gap-3">
              <Button
                variant="primary"
                size="lg"
                onClick={handleOpenInNewTab}
                disabled={loading}
              >
                {loading ? <Spinner size="sm" /> : <FaExternalLinkAlt className="me-2" />}
                Open Certificate
              </Button>
              
              <Button
                variant="success"
                size="lg"
                onClick={handleDownload}
                disabled={loading}
              >
                {loading ? <Spinner size="sm" /> : <FaDownload className="me-2" />}
                Download
              </Button>
            </div>

            <Alert variant="info" className="mt-4">
              <small>
                <FaExternalLinkAlt className="me-1" /> Click "Open Certificate" to view, print, or share in a new tab.
              </small>
            </Alert>
          </Card.Body>
        </Card>
      </div>
    );
  }

  // If batch is completed but certificate not yet issued
  if (batch?.student_status === 'completed' && !hasCertificate) {
    return (
      <div className="text-center py-5">
        <FaHourglassHalf size={60} className="text-warning mb-4" />
        <h3>Certificate Pending</h3>
        <p className="text-muted mb-4">
          You have successfully completed the batch!<br />
          Your certificate is being prepared. Please check back later.
        </p>
        <Alert variant="info" className="d-inline-block">
          <FaCheckCircle className="me-2 text-success" />
          Batch Status: Completed
        </Alert>
      </div>
    );
  }

  // If batch is in progress
  return (
    <div className="text-center py-5">
      <FaHourglassHalf size={60} className="text-info mb-4" />
      <h3>Certificate Coming Soon</h3>
      <p className="text-muted mb-4">
        Complete your batch to receive your certificate.
      </p>
      
      <div className="mt-4">
        <h5>Batch Progress: {batch?.progress || 0}%</h5>
        <div className="progress mx-auto" style={{ width: '300px', height: '10px' }}>
          <div
            className="progress-bar bg-success"
            style={{ width: `${batch?.progress || 0}%` }}
          />
        </div>
        <p className="mt-3 text-muted">
          {batch?.progress ? 100 - batch.progress : 100}% more to complete
        </p>
      </div>
    </div>
  );
};

export default StudentCertificateTab;